'use client'

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Check, Sparkles, Zap, Shield, Heart, ArrowRight, Crown } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { createClientComponentClient } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/Button'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { jsonToFeatures } from '@/types/typeHelpers'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

interface SubscriptionPlan {
  id: string
  plan_key: string | null
  name: string
  description: string | null
  price_monthly: number | null
  price_six_monthly: number | null
  price_yearly: number | null
  stripe_price_monthly_id: string | null
  stripe_price_six_monthly_id: string | null
  stripe_price_yearly_id: string | null
  features: string[]
  is_active: boolean
}

export default function SubscriptionPage() {
  const { t } = useTranslation()
  const { user, userProfile } = useAuth()
  const supabase = createClientComponentClient()
  const [billingInterval, setBillingInterval] = useState<'month' | '6month' | 'year'>('month')
  const [loadingPlan, setLoadingPlan] = useState<string | 'portal' | null>(null)
  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [loading, setLoading] = useState(true)

  // Determine user area
  const area = (() => {
    if (!userProfile) return 'regular'
    const ft = userProfile.facility_type?.toLowerCase() || ''
    const ap = userProfile.active_plan || ''

    if (ft === 'church') return 'church'
    if (ft === 'homeschool') return 'homeschool'
    if (ft === 'ngo' || ft === 'university_ngo') return 'ngo'
    if (ft === 'company' || ft === 'corporate') return 'company'
    if (ft === 'school' || ft === 'education') return 'education'
    if (ft === 'facility') return 'facility'
    if (ft === 'university' || userProfile.university_access || ap.includes('university') || ap.includes('scholar_pass')) return 'university'
    // If user already has an education plan show elite upgrade options
    if (ap.startsWith('homeschool_') || ap === 'edu_elite' || ap === 'edu_bundle') return 'homeschool'

    return 'regular'
  })()

  // Fetch active subscription plans
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const { data, error } = await supabase
          .from('subscription_plans')
          .select('*')
          .eq('is_active', true)
          .order('price_monthly', { ascending: true })

        if (error) {
          console.error('Error fetching plans:', error)
          return
        }

        const normalizedPlans = (data || [])
          .map((plan: any) => ({
            id: plan.id,
            plan_key: plan.plan_key,
            name: plan.name,
            description: plan.description,
            price_monthly: plan.price_monthly || 0,
            price_six_monthly: plan.price_six_monthly || 0,
            price_yearly: plan.price_yearly || 0,
            stripe_price_monthly_id: plan.stripe_price_monthly_id,
            stripe_price_six_monthly_id: plan.stripe_price_six_monthly_id,
            stripe_price_yearly_id: plan.stripe_price_yearly_id,
            features: jsonToFeatures(plan.features),
            is_active: plan.is_active ?? true
          }))

        // SMART FILTERING
        const filtered = normalizedPlans.filter(plan => {
          const pk = plan.plan_key || ''
          const isPersonal = ['sage-premium', 'sage-family', 'sage-text', 'sage-voice', 'free'].includes(pk)
          const isChurchPlan = pk === 'sage-church-seat'
          const isHomeschoolPlan = pk.startsWith('homeschool_') || pk === 'edu_elite' || pk === 'edu_bundle'
          const isUniversityPlan = pk.startsWith('university_') || pk.startsWith('scholar_pass_')
          const isEducationPlan = pk === 'sage-facility-seat'
          const isNGOPlan = pk.startsWith('ngo_pass_')
          const isCompanyPlan = pk === 'university_corporate'

          const isAdmin = userProfile?.facility_role === 'admin'

          // Church members and admins have access to the $7 church seat plan
          if (area === 'church') return isChurchPlan || isPersonal
          // Homeschool admins see all three education tiers (standard + elite + bundle)
          if (area === 'homeschool') return isAdmin ? isHomeschoolPlan : isPersonal
          if (area === 'university') return isUniversityPlan || isPersonal
          if (area === 'ngo') return isAdmin ? isNGOPlan : isPersonal
          if (area === 'company') return isAdmin ? isCompanyPlan : isPersonal
          if (area === 'education' || area === 'facility') return isAdmin ? isEducationPlan : isPersonal

          return isPersonal || isUniversityPlan // Default: Regular user see both personal and university
        })

        setPlans(filtered)
      } catch (error: any) {
        console.error('Fetch plans catch:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchPlans()
  }, [supabase])

  /** Convert billing interval for the billing function */
  const intervalParam = billingInterval === '6month' ? '6month' : billingInterval

  const startCheckout = async (planKey: string) => {
    setLoadingPlan(planKey)
    try {
      if (!user) throw new Error('User not authenticated')
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !session) throw new Error('No valid session found')

      const { data, error } = await supabase.functions.invoke('billing', {
        body: {
          action: 'checkout',
          plan: planKey,
          interval: intervalParam,
          destination: planKey === 'sage-church-seat' ? 'church_member' : undefined
        },
        headers: { Authorization: `Bearer ${session.access_token}` },
      })

      if (error) throw error
      if (data?.url) window.location.href = data.url
    } catch (e) {
      console.error('Checkout error', e)
      alert('Unable to start checkout. Please try again.')
    } finally {
      setLoadingPlan(null)
    }
  }

  const openPortal = async () => {
    setLoadingPlan('portal')
    try {
      if (!user) throw new Error('User not authenticated')
      const { data: { session }, error: sessionError } = await supabase.auth.getSession()
      if (sessionError || !session) throw new Error('No valid session found')

      const { data, error } = await supabase.functions.invoke('billing', {
        body: { action: 'portal' },
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
      if (error) throw error
      if (data?.url) window.location.href = data.url
    } catch (e) {
      console.error('Portal error', e)
      alert('Unable to open billing portal.')
    } finally {
      setLoadingPlan(null)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-mesh flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 100 }
    }
  } as const

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price)
  }

  return (
    <div className="min-h-screen bg-mesh pb-20 md:pb-32">
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-10 md:py-24 relative z-10">
        {/* Header Section */}
        <div className="text-center mb-16 md:mb-24 space-y-8">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-emerald-600 text-white text-xs md:text-sm font-black tracking-widest uppercase shadow-lg shadow-emerald-600/20"
          >
            <Sparkles size={16} />
            {t(`subscriptionPage.badge.${area}`)}
          </motion.div>

          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="text-4xl sm:text-6xl md:text-8xl font-black text-white tracking-tighter leading-[0.95] mb-8"
          >
            {t(`subscriptionPage.title.${area}`)}
          </motion.h1>

          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="text-xl md:text-3xl text-slate-400 font-medium max-w-3xl mx-auto leading-relaxed px-4"
          >
            {t(`subscriptionPage.subtitle.${area}`)}
          </motion.p>

          {/* Billing Toggle — 3 options */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mt-12 flex items-center justify-center"
          >
            <div className="inline-flex items-center gap-1 bg-slate-100 rounded-2xl p-1.5">
              {([
                { key: 'month' as const, label: t('subscriptionPage.monthly'), badge: null },
                { key: '6month' as const, label: t('subscriptionPage.sixMonthly', '6-Monthly'), badge: t('subscriptionPage.save10', 'Save 10%') },
                { key: 'year' as const, label: t('subscriptionPage.yearly'), badge: t('subscriptionPage.save20') },
              ]).map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setBillingInterval(opt.key)}
                  className={clsx(
                    'h-10 px-5 rounded-xl font-black text-sm transition-all flex items-center gap-2',
                    billingInterval === opt.key ? 'bg-slate-900 text-white shadow-md' : 'text-slate-400 hover:text-white'
                  )}
                >
                  {opt.label}
                  {opt.badge && billingInterval === opt.key && (
                    <span className="bg-emerald-500 text-white text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest">{opt.badge}</span>
                  )}
                </button>
              ))}
            </div>
          </motion.div>

          {userProfile?.subscription_status === 'premium' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-12">
              <Button
                onClick={openPortal}
                disabled={loadingPlan === 'portal'}
                variant="glass"
                size="xl"
                className="font-black h-16 md:h-20 px-10 rounded-2xl md:rounded-[1.8rem]"
              >
                {loadingPlan === 'portal' ? 'Opening Portal...' : t('subscriptionPage.manage')}
              </Button>
            </motion.div>
          )}
        </div>

        {/* Plans Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8"
        >
          {plans.map((plan) => {
            const isElite = plan.plan_key === 'edu_elite' || plan.plan_key === 'edu_bundle'
            const isHighlighted = plan.plan_key === 'sage-premium' || plan.plan_key === 'edu_bundle'
            const currentMonthlyPrice = billingInterval === 'month'
              ? plan.price_monthly
              : billingInterval === '6month'
                ? (plan.price_six_monthly || 0) / 6
                : (plan.price_yearly || 0) / 12

            return (
              <motion.div
                key={`plan-${plan.id}`}
                variants={itemVariants}
                className={clsx(
                  "glass-card !p-8 sm:!p-10 flex flex-col relative overflow-hidden group min-h-[480px] md:min-h-[580px]",
                  plan.plan_key === 'edu_bundle' ? "ring-2 shadow-2xl" : '',
                  plan.plan_key === 'edu_elite' ? 'ring-1' : '',
                  isHighlighted && plan.plan_key === 'sage-premium' ? "ring-2 ring-emerald-500 shadow-emerald-500/20 shadow-2xl bg-white/95" : '',
                  !isHighlighted && !isElite ? "border-white/60" : ''
                )}
                style={plan.plan_key === 'edu_bundle' ? { boxShadow: '0 20px 60px rgba(201,168,76,0.15)', background: 'linear-gradient(145deg, #1a1510dd, #241d0add)', backdropFilter: 'blur(20px)', border: '1.5px solid rgba(201,168,76,0.35)' } : plan.plan_key === 'edu_elite' ? { background: 'rgba(201,168,76,0.04)', border: '1px solid rgba(201,168,76,0.2)' } : undefined}
              >
                {isHighlighted && plan.plan_key === 'sage-premium' && (
                  <div className="absolute top-0 right-0 bg-emerald-500 text-white text-[10px] font-black uppercase tracking-[0.2em] px-6 py-2 rounded-bl-2xl">
                    Most Popular
                  </div>
                )}
                {plan.plan_key === 'edu_bundle' && (
                  <div className="absolute top-0 right-0 text-[10px] font-black uppercase tracking-[0.2em] px-5 py-2 rounded-bl-2xl flex items-center gap-1.5"
                    style={{ background: 'linear-gradient(135deg, #7a5c20, #C9A84C)', color: '#0A0A0F' }}>
                    <Crown size={10} /> Best Value
                  </div>
                )}
                {plan.plan_key === 'edu_elite' && (
                  <div className="absolute top-0 right-0 text-[10px] font-black uppercase tracking-[0.2em] px-5 py-2 rounded-bl-2xl flex items-center gap-1.5"
                    style={{ background: 'rgba(201,168,76,0.15)', color: '#C9A84C', border: '0px' }}>
                    <Crown size={10} /> Elite
                  </div>
                )}

                <div className="mb-10">
                  <h3 className={clsx('text-3xl md:text-4xl font-black mb-2', isElite ? 'text-white' : 'text-white')}>{plan.name}</h3>
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl md:text-6xl font-black text-white tracking-tighter">
                      {formatPrice(currentMonthlyPrice || 0)}
                    </span>
                    <span className="text-xl text-slate-400 font-black">/mo</span>
                  </div>
                  {billingInterval === 'year' && plan.price_yearly ? (
                    <p className="text-sm text-emerald-400 font-black mt-2 uppercase tracking-widest">
                      Billed annually ({formatPrice(plan.price_yearly)}/yr)
                    </p>
                  ) : billingInterval === '6month' && plan.price_six_monthly ? (
                    <p className="text-sm text-emerald-400 font-black mt-2 uppercase tracking-widest">
                      Billed every 6 months ({formatPrice(plan.price_six_monthly)})
                    </p>
                  ) : null}
                </div>

                <ul className="space-y-4 mb-10 flex-1">
                  {plan.features.map((feature, fIdx) => (
                    <li key={`plan-${plan.id}-feat-${fIdx}`} className="flex items-start gap-4">
                      <div className="mt-1 w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-500/20">
                        <Check className="w-3.5 h-3.5 text-white" strokeWidth={4} />
                      </div>
                      <span className="text-lg font-bold text-white leading-tight">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  variant={isHighlighted ? 'premium' : 'glass'}
                  size="xl"
                  className="w-full h-16 md:h-20 text-xl font-black rounded-2xl md:rounded-[1.8rem] transition-all group"
                  onClick={() => {
                    if (plan.plan_key && plan.plan_key !== 'free') {
                      startCheckout(plan.plan_key)
                    }
                  }}
                  disabled={loadingPlan === plan.plan_key || plan.plan_key === 'free'}
                >
                  {loadingPlan === plan.plan_key ? (
                    <LoadingSpinner />
                  ) : plan.plan_key === 'free' ? (
                    t('subscriptionPage.current')
                  ) : (
                    <>
                      {t('subscriptionPage.choose')}
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </Button>
              </motion.div>
            )
          })}
        </motion.div>

        {/* Trust Footer */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20 md:mt-32 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16 text-center border-t border-slate-200/50 pt-16 md:pt-24 px-4"
        >
          {[
            { icon: Zap, title: t('subscriptionPage.setup'), desc: t('subscriptionPage.setupDesc') },
            { icon: Shield, title: t('subscriptionPage.privacy'), desc: t('subscriptionPage.privacyDesc') },
            { icon: Heart, title: t('subscriptionPage.seniorFirst'), desc: t('subscriptionPage.seniorFirstDesc') }
          ].map((item, tIdx) => (
            <div key={`trust-${tIdx}`} className="space-y-6">
              <div className="w-20 h-20 md:w-24 md:h-24 bg-white rounded-[2rem] shadow-xl shadow-black/[0.04] flex items-center justify-center mx-auto transition-transform hover:scale-110">
                <item.icon className="text-emerald-400 w-10 h-10 md:w-12 md:h-12" />
              </div>
              <div>
                <h4 className="text-2xl md:text-3xl font-black text-white mb-2">{item.title}</h4>
                <p className="text-lg md:text-xl text-slate-400 font-medium leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  )
}
